import {graphql} from 'gatsby'
import he from 'he'
import React from 'react'
import styled from 'styled-components'

import {ApplicationForm} from '@/components/application-form'
import Button from '@/components/button'
import Footer from '@/components/footer'
import Header from '@/components/header'
import Image from '@/components/image'
import PrismicLink from '@/components/prismic-link'
import RichText from '@/components/rich-text'
import SEO from '@/components/seo'
import {Main} from '@/components/skip-to-content'
import SmoothScrollLink from '@/components/smooth-scroll-link'
import {Text, getTextVariantStyles} from '@/components/text'
import {JobsGrid} from '@/sections/jobs-grid'
import {Steps} from '@/sections/steps'
import TitleWithAccordion from '@/sections/title-with-accordion'
import {BackgroundWrapper, Layout, Section} from '@/styles/grid'
import {breakpoints, tokens} from '@/theme'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import mq from '@/utils/media-query'
import {withPrismicPreview} from '@/utils/with-prismic-preview'

import type {SingleJobQuery} from '@/graphql-types'
import type {GatsbyHeadProps} from '@/types/gatsby-head'
import type {PageProps} from 'gatsby'

type Props = PageProps<SingleJobQuery>

const Job = (props: Props) => {
    const [isSubmitted, setIsSubmitted] = React.useState(false)
    const job = props.data.allGreenhouseJob.nodes[0]
    const jobId = job.id.replace('Greenhouse__Job__', '')
    const data = props.data.prismicJobPage.data
    const steps = props.data.prismicJobListing?.data

    if (!data) {
        return handleNoPrismicData()
    }

    const faqItems = data.faq.map((item) => {
        return {
            title: item.question,
            body: item.answer.richText,
        }
    })

    return (
        <>
            <Header hideRightNav />

            <Main>
                <Section data-testid="job-title">
                    <TitleWrapper>
                        <RichText content={job.title} $variant="heading-large" forwardedAs="h1" />
                    </TitleWrapper>
                </Section>

                <MainContent as={Section}>
                    <StickySidebar>
                        <JobMeta>
                            <JobMetaItem>
                                <Text>{data.location_label}</Text>
                                <Text $variant="paragraph-large" $color="placeholder">
                                    {job.offices.map((office) => office.name).join(', ')}
                                </Text>
                            </JobMetaItem>
                            <JobMetaItem>
                                <Text>{data.team_label}</Text>
                                <Text $variant="paragraph-large" $color="placeholder">
                                    {job.departments[0].name}
                                </Text>
                            </JobMetaItem>
                        </JobMeta>

                        <Button as={SmoothScrollLink} selector="#application-submission-section">
                            {data.apply_now_label}
                        </Button>
                    </StickySidebar>

                    <div>
                        <StyledInnerHTML
                            dangerouslySetInnerHTML={{__html: he.decode(job.content)}}
                        />
                        <ExtraBlock>
                            <Text as="h3" $variant="title-base">
                                {data.extra_questions_title}
                            </Text>
                            <Button as={SmoothScrollLink} selector="#faq">
                                {data.extra_questions_button}
                            </Button>
                        </ExtraBlock>
                    </div>
                </MainContent>

                <BackgroundWrapper $color="muted">
                    <Section id="application-submission-section">
                        <Layout $variant="two-asymmetrical">
                            {isSubmitted ? (
                                <div data-testid="job-application-success">
                                    <StyledFormTitle $variant="heading-large">
                                        {data.form[0].thank_you_title}
                                    </StyledFormTitle>
                                    <StyledFormText $variant="paragraph-large">
                                        {data.form[0].thank_you_content}
                                    </StyledFormText>
                                    {data.form[0].thank_you_link &&
                                        data.form[0].thank_you_link_text && (
                                            <Button
                                                as={PrismicLink}
                                                $variant="outline"
                                                {...data.form[0].thank_you_link}
                                            >
                                                {data.form[0].thank_you_link_text}
                                            </Button>
                                        )}
                                </div>
                            ) : (
                                <div>
                                    <StyledFormTitle $variant="heading-large">
                                        {data.form[0].title}
                                    </StyledFormTitle>
                                    <StyledFormText $variant="paragraph-large">
                                        {data.form[0].content}
                                    </StyledFormText>
                                    <Image image={data.form[0].image} />
                                </div>
                            )}
                            {isSubmitted ? (
                                <Image image={data.form[0].thank_you_image} />
                            ) : (
                                <ApplicationForm
                                    jobId={jobId}
                                    questions={job.questions}
                                    setIsSubmitted={setIsSubmitted}
                                />
                            )}
                        </Layout>
                    </Section>
                </BackgroundWrapper>

                <JobsGrid
                    headline={data.more_jobs[0].title}
                    text={data.more_jobs[0].description}
                    ctaText={data.more_jobs[0].cta_text}
                />

                {steps && (
                    <Steps
                        showCount
                        title={steps.steps_title}
                        items={steps.steps}
                        ctaLink={steps.cta_link}
                        ctaText={steps.cta_text}
                    />
                )}

                <TitleWithAccordion
                    id="faq"
                    title={data.faq_title}
                    items={faqItems}
                    dataTestId="faq-section"
                />
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<SingleJobQuery>) => {
    const pageData = data.prismicJobPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={data.allGreenhouseJob.nodes[0].title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image.url}
        />
    )
}

const TitleWrapper = styled.div`
    display: inline-block;

    ${mq.min('lg')} {
        max-width: ${breakpoints.md}px;
    }
`

const MainContent = styled.div`
    display: grid;
    gap: ${tokens.spacing28};

    ${mq.min('sm')} {
        grid-auto-flow: column;
        grid-template-columns: 300px 1fr;
    }

    ${mq.min('md')} {
        gap: 100px;
    }
`

const StickySidebar = styled.aside`
    top: 8rem;
    display: grid;
    align-self: start;
    width: 100%;
    gap: ${tokens.spacing40};
    padding: ${tokens.spacing20};
    background: ${tokens.shade200};
    border-radius: ${tokens.arc8};

    ${mq.min('sm')} {
        position: sticky;
    }
`

const JobMeta = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
`

const JobMetaItem = styled.div`
    display: grid;
    gap: ${tokens.spacing8};
`

const ExtraBlock = styled.div`
    display: grid;
    justify-items: start;
    gap: ${tokens.spacing20};
`

const StyledInnerHTML = styled.div`
    h2,
    h3 {
        ${getTextVariantStyles('title-base')}
        margin-bottom: ${tokens.spacing20};
    }

    p,
    ul,
    ol {
        ${getTextVariantStyles('paragraph-large')}
        margin-bottom: ${tokens.spacing40};
    }
`

const StyledFormTitle = styled(Text)`
    margin-block-end: ${tokens.spacing20};
`

const StyledFormText = styled(Text)`
    margin-bottom: ${tokens.spacing24};
`

export const query = graphql`
    query SingleJob(
        $id: String!
        $locale: String!
        $imageBreakpoints: [Int]
        $twoColumnsAsymmetric: String
    ) {
        prismicJobPage(lang: {eq: $locale}) {
            data {
                meta_description
                open_graph_image {
                    url
                }
                location_label
                team_label
                apply_now_label
                extra_questions_title
                extra_questions_button
                faq_title
                faq {
                    question
                    answer {
                        richText
                    }
                }
                more_jobs {
                    title
                    description
                    cta_text
                }
                form {
                    title
                    content
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData(
                            breakpoints: $imageBreakpoints
                            sizes: $twoColumnsAsymmetric
                        )
                    }
                    thank_you_title
                    thank_you_content
                    thank_you_link {
                        ...PrismicLink
                    }
                    thank_you_link_text
                    thank_you_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData(
                            breakpoints: $imageBreakpoints
                            sizes: $twoColumnsAsymmetric
                        )
                    }
                }
            }
        }

        prismicJobListing(lang: {eq: $locale}) {
            data {
                cta_text
                cta_link {
                    ...PrismicLink
                }
                steps_title
                steps {
                    heading
                    content
                    background_color
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData(
                            breakpoints: [750, 1080, 1440]
                            sizes: "(max-width: 767px) 100vw, (max-width: 991px) 40vw, 55vw"
                        )
                    }
                }
            }
        }

        allGreenhouseJob(filter: {id: {eq: $id}}) {
            nodes {
                id
                title
                content
                departments {
                    name
                }
                offices {
                    name
                }
                questions {
                    label
                    required
                    fields {
                        name
                        type
                        values {
                            label
                            value
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(Job)
